import { useState } from 'react';
import { Button } from '@flintmint/web-shared/components/button';

import type { FlowInput } from '@/services/flows-service';
import { usePromptSlugValidation, type PromptSlugErrors } from '../../use-prompt-slug-validation';
import { SlugField } from '../slug-field/slug-field';

import styles from './add-flow-row.module.scss';

interface AddFlowRowProps {
  onAdd: (data: FlowInput) => Promise<void>;
  onCancel: () => void;
}

export const AddFlowRow = ({ onAdd, onCancel }: AddFlowRowProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState('');
  const [criteria, setCriteria] = useState('');
  const [moduleSelectionPromptSlug, setModuleSelectionPromptSlug] = useState('');
  const [corePromptSlug, setCorePromptSlug] = useState('');
  const [slugErrors, setSlugErrors] = useState<PromptSlugErrors>({});
  const validatePromptSlugs = usePromptSlugValidation();

  const isValid = name.trim() && criteria.trim() && moduleSelectionPromptSlug.trim() && corePromptSlug.trim();

  const handleSlugChange = (field: keyof PromptSlugErrors, value: string) => {
    const setSlug = field === 'corePromptSlug' ? setCorePromptSlug : setModuleSelectionPromptSlug;
    setSlug(value);
    setSlugErrors({ ...slugErrors, [field]: undefined });
  };

  const handleAdd = async () => {
    if (!isValid) return;
    setIsSaving(true);
    try {
      const slugs = { moduleSelectionPromptSlug: moduleSelectionPromptSlug.trim(), corePromptSlug: corePromptSlug.trim() };
      const errors = await validatePromptSlugs(slugs);
      setSlugErrors(errors);
      if (Object.keys(errors).length > 0) return;

      await onAdd({ name: name.trim(), criteria: criteria.trim(), ...slugs, enabled: true });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.row} data-editing data-adding data-testid="flow-add-row">
      <input
        className={styles.nameInput}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name (e.g. Medical)"
        disabled={isSaving}
        data-testid="flow-name-input"
      />
      <textarea
        className={styles.criteriaTextarea}
        value={criteria}
        onChange={(e) => setCriteria(e.target.value)}
        placeholder="Routing criteria fed to the router"
        rows={3}
        disabled={isSaving}
        data-testid="flow-criteria-input"
      />
      <SlugField
        value={moduleSelectionPromptSlug}
        onChange={(value) => handleSlugChange('moduleSelectionPromptSlug', value)}
        placeholder="Module selection prompt slug"
        disabled={isSaving}
        error={slugErrors.moduleSelectionPromptSlug}
        testId="flow-selection-slug"
      />
      <SlugField
        value={corePromptSlug}
        onChange={(value) => handleSlugChange('corePromptSlug', value)}
        placeholder="Core prompt slug"
        disabled={isSaving}
        error={slugErrors.corePromptSlug}
        testId="flow-core-slug"
      />
      <span />
      <span />
      <span />
      <div className={styles.actions}>
        <Button
          size="small"
          color="success"
          onClick={handleAdd}
          loading={isSaving}
          disabled={!isValid}
          data-testid="flow-add-confirm-btn"
        >
          Add
        </Button>
        <Button size="small" appearance="clear" onClick={onCancel} disabled={isSaving} data-testid="flow-add-cancel-btn">
          Cancel
        </Button>
      </div>
    </div>
  );
};
